import ArcNumber from '@arcxgame/contracts/dist/src/utils/ArcNumber'
import { eqIgnoreCase } from '@arcxmoney/arcx-core'
import { Erc20Details } from '@arcxmoney/arcx-react'
import { useWeb3React } from '@web3-react/core'
import { BigNumber } from 'ethers'
import { parseEther } from 'ethers/lib/utils'
import React, { useContext } from 'react'
import { BASE } from 'src/constants/crypto'
import { ArcContext } from 'src/contexts/ArcContext'
import { getReadableNumber } from 'src/helpers/getReadableNumber'
import { Synth } from 'src/services/GraphService'
import { getUniqueArray } from 'src/utils/isFirstOccurred'
import { ArcButton } from '../ArcButton'
import { Input } from '../Input'
import { Title } from '../Title'
import './Manage.scss'
import { ManageInfo } from './ManageInfo'

interface IProps {
  actionTitle: string
  availableSynths: Synth[]
  changeDescription?: string
  collateralBalance: BigNumber
  collateralDetails: Erc20Details
  collateralFieldTitle: string
  existingVaultNotice?: JSX.Element
  currentCollateralRatio?: BigNumber
  currentLiquidationPrice?: BigNumber
  defaultCollateral?: BigNumber
  defaultSynthetic?: BigNumber
  description?: string | React.ReactElement
  isEnoughTokens: boolean
  maxSyntheticAmount: BigNumber
  onExitClick?: () => void
  onLiquidateClick?: () => void
  onPrimaryClick: () => void
  positionOwner?: string
  setEnteredCollateralAmount: (value: BigNumber) => void
  setEnteredSyntheticAmount: (value: BigNumber) => void
  setSynth?: React.Dispatch<React.SetStateAction<Synth>>
  synth: Synth
  syntheticFieldTitle: string
  title: string
  debtUnpayable?: boolean
}

export function Manage({
  actionTitle,
  changeDescription,
  collateralDetails,
  collateralBalance,
  collateralFieldTitle,
  existingVaultNotice,
  currentCollateralRatio,
  currentLiquidationPrice,
  defaultCollateral,
  defaultSynthetic,
  description,
  isEnoughTokens,
  maxSyntheticAmount,
  onExitClick,
  onLiquidateClick,
  onPrimaryClick,
  positionOwner,
  setEnteredCollateralAmount,
  setEnteredSyntheticAmount,
  setSynth,
  synth,
  availableSynths,
  syntheticFieldTitle,
  title,
  debtUnpayable,
}: IProps) {
  const { account } = useWeb3React()
  const { getSyntheticBalance, synths, getPrice, arc, network, isLoading } = useContext(ArcContext)
  const syntheticBalance = getSyntheticBalance(synth.syntheticAddress)
  const price = getPrice(synth.oracle)

  const onCollateralChanged =
    setSynth &&
    ((asset: string) =>
      setSynth((prev) =>
        prev.collateralAddress === asset ? prev : getSynthByCollateral(synths, prev, asset),
      ))

  const onSyntheticChanged =
    setSynth &&
    ((asset: string) =>
      setSynth((prev) =>
        prev.collateralAddress === asset ? prev : getSynthBySynthetic(synths, prev, asset),
      ))

  const isAuthorized = !positionOwner || eqIgnoreCase(positionOwner, account)

  const isPrimaryArcButtonEnabled =
    isEnoughTokens &&
    currentCollateralRatio.gte(synth.collateralRatio.mul(100).div(BASE)) &&
    isAuthorized &&
    !isLoading

  const isExitBtnEnabled = isAuthorized && !isLoading && defaultCollateral?.gt(BigNumber.from(0))

  return (
    <div className="manage">
      <Title title={title} className="manage__title" description={description} />
      <div className="manage__container">
        <Input
          assets={availableSynths.map(({ collateralAddress, collateral }) => ({
            value: collateralAddress,
            label: collateral,
          }))}
          onAssetChanged={onCollateralChanged}
          label={collateralFieldTitle}
          balance={collateralBalance}
          selectedAsset={synth.collateralAddress}
          setAmount={setEnteredCollateralAmount}
          maxAmount={collateralBalance}
          defaultValue={defaultCollateral}
          disabled={!onPrimaryClick}
          decimals={collateralDetails.decimals}
        />
        {existingVaultNotice}
        <Input
          assets={getUniqueArray(
            availableSynths.map(({ syntheticAddress, synthetic }) => ({
              value: syntheticAddress,
              label: synthetic,
            })),
            'value',
          )}
          onAssetChanged={onSyntheticChanged}
          label={syntheticFieldTitle}
          balance={syntheticBalance}
          selectedAsset={synth.syntheticAddress}
          setAmount={setEnteredSyntheticAmount}
          maxAmount={maxSyntheticAmount}
          defaultValue={defaultSynthetic}
          disabled={!onPrimaryClick}
          boundaryRatio={boundaryRatio}
          mode="with-slider"
        />
        <p className="manage__change-description">{changeDescription}</p>
        <ManageInfo
          synth={synth}
          currentLiquidationPrice={`${getReadableNumber(currentLiquidationPrice)}`}
          currentCollateralRatio={`${currentCollateralRatio?.toString()}`}
          minimumCollateralRatio={`${getReadableNumber(synth.collateralRatio.mul(100))}`}
          currentPrice={getReadableNumber(price)}
        />
        {isAuthorized && (
          <ArcButton
            onClick={onPrimaryClick}
            loading={isLoading}
            disabled={!isPrimaryArcButtonEnabled}
            name={actionTitle}
          />
        )}
        {isAuthorized && onExitClick && (
          <ArcButton
            onClick={onExitClick}
            type="secondary"
            loading={isLoading}
            disabled={!isExitBtnEnabled || debtUnpayable}
            name={debtUnpayable ? 'You don\'t have enough STABLEx to close this position.' : 'Exit'}
          />
        )}
        {onLiquidateClick && (
          <ArcButton
            onClick={onLiquidateClick}
            type="secondary"
            loading={isLoading}
            name="Liquidate"
          />
        )}
        {network !== 1 && (
          <ArcButton
            onClick={() => arc.mintTestCollateral(synth.name, parseEther('10'), account)}
            type="secondary"
            name="Mint Test Collateral"
          />
        )}
      </div>
    </div>
  )
}

const boundaryRatio = ArcNumber.new(1).mul(8).div(10)

function getSynthByCollateral(synths: Synth[], synth: Synth, address: string) {
  const filteredSynths = synths.filter(({ collateralAddress }) => collateralAddress === address)
  const result = filteredSynths.find(
    ({ syntheticAddress }) => syntheticAddress === synth.syntheticAddress,
  )
  return result || filteredSynths[0]
}

function getSynthBySynthetic(synths: Synth[], synth: Synth, address: string) {
  const filteredSynths = synths.filter(({ syntheticAddress }) => syntheticAddress === address)
  const result = filteredSynths.find(
    ({ collateralAddress }) => collateralAddress === synth.collateralAddress,
  )
  return result || filteredSynths[0]
}
